import {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

// Two separate contexts so the (potentially 80+) SlideEdit instances can
// subscribe ONLY to the stable "actions" surface (setters + register*/trigger*
// + refs) and never re-render when volatile UI state (hover, selection, slide
// title) changes. Bundling everything in one provider value — which was rebuilt
// as a fresh object on every render — meant a single hover/keystroke re-rendered
// every consumer, bypassing React.memo entirely. Only the single-instance
// consumers that actually display that state (Toolbar, BlockTree) subscribe to
// the volatile "state" context.
const SlideActionsContext = createContext();
const SlideStateContext = createContext();

export const useSlideActions = () => useContext(SlideActionsContext);
export const useSlideState = () => useContext(SlideStateContext);

// Back-compat: consumers that need both surfaces (Toolbar, BlockTree) keep
// using useSlideContext() unchanged. They re-render on volatile state changes,
// which is correct for them. SlideEdit must use useSlideActions() instead so it
// does not subscribe to the volatile state at all.
export const useSlideContext = () => ({
  ...useSlideActions(),
  ...useSlideState(),
});

export const SlideContextProvider = ({ children }) => {
  const [activeSlideTitle, setActiveSlideTitle] = useState("");
  const [modifiedSlideTitle, setModifiedSlideTitle] = useState(false);
  const [activeSlideLayout, setActiveSlideLayout] = useState("");
  // const [hasPresentationAudio, setHasPresentationAudio] = useState(false);
  const [hasSlideAudio, setHasSlideAudio] = useState(false);
  const [selectModeStatus, setSelectModeStatus] = useState(false);
  const [blockOutlines, setBlockOutlines] = useState(false);
  const [showBlockTree, setShowBlockTree] = useState(false);
  const [activeSlideGrid, setActiveSlideGrid] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [activeSelectedBlocks, setActiveSelectedBlocks] = useState(new Set());
  const [activeSelectedGroups, setActiveSelectedGroups] = useState(new Set());
  const [activeAvailableTools, setActiveAvailableTools] = useState([]);
  const [activeCustomLayoutLock, setActiveCustomLayoutLock] = useState(false);

  const currentEditorInstanceRef = useRef(null);
  const currentSlideRef = useRef(null);
  const toggleGridFunctionRef = useRef(null);
  const modifySlideTitleFunctionRef = useRef(null);
  const groupFunctionRef = useRef(null);
  const ungroupFunctionRef = useRef(null);
  const blockOutlinesFuncitonRef = useRef(null);
  const blockTreeUpdateFunctionRef = useRef(null);
  const blockOrderFunctionRef = useRef(null);
  const blockDuplicateFunctionRef = useRef(null);
  const selectClickHandlerRef = useRef(null);
  const startSelectModeFunctionRef = useRef(null);
  const endSelectModeFunctionRef = useRef(null);
  const deletBlocksFunctionRef = useRef(null);
  const addBlockFunctionRef = useRef(null);
  const hideBlockFunctionRef = useRef(null);
  const hideGroupFunctionRef = useRef(null);
  const lockSlideLayoutFunctionRef = useRef(null);
  const moveBlockToGroupFunctionRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if(activeCustomLayoutLock) return;
    if (e.shiftKey) {
      triggerStartSelectMode();
    }

    if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "g") {
      e.stopPropagation();
      e.preventDefault();
      triggerGroup();
    }

    if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "g") {
      e.stopPropagation();
      e.preventDefault();
      triggerUngroup();
    }

    if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "d") {
      e.stopPropagation();
      e.preventDefault();
      triggerBlockDuplicate();
    }

    if (e.ctrlKey && e.key === "Delete") {
      e.stopPropagation();
      e.preventDefault();
      triggerDeleteBlocks();
    }
  }, [activeCustomLayoutLock]);

  const handleKeyUp = useCallback((e) => {
    if (!e.shiftKey) {
      triggerEndSelectMode();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line
  }, [handleKeyDown]);

  // Registers
  const registerToggleGridFunction = (fn) => {
    toggleGridFunctionRef.current = fn;
  };

  const registerModifySlideTitle = (fn) => {
    modifySlideTitleFunctionRef.current = fn;
  };

  const registerStartSelectModeFunction = (fn) => {
    startSelectModeFunctionRef.current = fn;
  };

  const registerEndSelectModeFunction = (fn) => {
    endSelectModeFunctionRef.current = fn;
  };

  const registerGroupFunction = (fn) => {
    groupFunctionRef.current = fn;
  };

  const registerUngroupFunction = (fn) => {
    ungroupFunctionRef.current = fn;
  };

  const registerToggleBlockOutlinesFunction = (fn) => {
    blockOutlinesFuncitonRef.current = fn;
  };

  const registerCurrentEditorInstance = (editorInstance) => {
    currentEditorInstanceRef.current = editorInstance;
  };

  const registerCurrentSlideRef = (slideRef) => {
    currentSlideRef.current = slideRef;
  };

  const registerBlockTreeUpdateFunction = (fn) => {
    blockTreeUpdateFunctionRef.current = fn;
  };

  const registerBlockOrderFunction = (fn) => {
    blockOrderFunctionRef.current = fn;
  };

  const registerBlockDuplicateFunction = (fn) => {
    blockDuplicateFunctionRef.current = fn;
  };

  const registerSelectClickHandler = (fn) => {
    selectClickHandlerRef.current = fn;
  };

  const registerDeleteBlocksFunction = (fn) => {
    deletBlocksFunctionRef.current = fn;
  };

  const registerAddBlockFunction = (fn) => {
    addBlockFunctionRef.current = fn;
  };

  const registerHideBlockFunction = (fn) => {
    hideBlockFunctionRef.current = fn;
  };

  const registerHideGroupFunction = (fn) => {
    hideGroupFunctionRef.current = fn;
  };

  const registerLockSlideLayoutFunction = (fn) => {
    lockSlideLayoutFunctionRef.current = fn;
  };

  const registerMoveBlockToGroupFunction = (fn) => {
    moveBlockToGroupFunctionRef.current = fn;
  };

  // Triggers
  const triggerToggleGrid = () => {
    if (toggleGridFunctionRef.current) {
      toggleGridFunctionRef.current();
    }
  };

  const triggerModifySlideTitle = (value) => {
    if (modifySlideTitleFunctionRef.current) {
      modifySlideTitleFunctionRef.current(value);
    }
  };

  const triggerStartSelectMode = () => {
    if (startSelectModeFunctionRef.current) {
      startSelectModeFunctionRef.current();
    }
  };

  const triggerEndSelectMode = () => {
    if (endSelectModeFunctionRef.current) {
      endSelectModeFunctionRef.current();
    }
  };

  const triggerGroup = () => {
    if (groupFunctionRef.current) {
      groupFunctionRef.current();
    }
  };

  const triggerUngroup = () => {
    if (ungroupFunctionRef.current) {
      ungroupFunctionRef.current();
    }
  };

  const triggerBlockOutlines = () => {
    if (blockOutlinesFuncitonRef.current) {
      blockOutlinesFuncitonRef.current();
    }
  };

  const triggerBlockTreeUpdate = () => {
    if (blockTreeUpdateFunctionRef.current) {
      blockTreeUpdateFunctionRef.current();
    }
  };

  const triggerBlockOrder = (value) => {
    if (blockOrderFunctionRef.current) {
      blockOrderFunctionRef.current(value);
    }
  };

  const triggerBlockDuplicate = () => {
    if (blockDuplicateFunctionRef.current) {
      blockDuplicateFunctionRef.current();
    }
  };

  const triggerSelectClick = (blockId, groupId) => {
    if (selectClickHandlerRef.current) {
      selectClickHandlerRef.current(blockId, groupId);
    }
  };

  const triggerDeleteBlocks = (blockIds) => {
    if (deletBlocksFunctionRef.current) {
      deletBlocksFunctionRef.current(blockIds);
    }
  };

  const triggerAddBlock = (blockType) => {
    if (addBlockFunctionRef.current) {
      addBlockFunctionRef.current(blockType);
    }
  };

  const triggerHideBlock = (blockId) => {
    if (hideBlockFunctionRef.current) {
      hideBlockFunctionRef.current(blockId);
    }
  };

  const triggerHideGroup = (groupId) => {
    if (hideGroupFunctionRef.current) {
      hideGroupFunctionRef.current(groupId);
    }
  };

  const triggerLockSlideLayout = () => {
    if (lockSlideLayoutFunctionRef.current) {
      lockSlideLayoutFunctionRef.current();
    }
  };

  const triggerMoveBlockToGroup = (blockId, newGroupId) => {
    if(moveBlockToGroupFunctionRef.current){
      moveBlockToGroupFunctionRef.current(blockId, newGroupId);
    }
  }

  // Stable action surface — its identity must NEVER change after mount, or the
  // 80 SlideEdit consumers would re-render. Setters returned by useState are
  // already stable, the refs are stable, and the register/trigger closures only
  // ever read/write the stable refs above, so freezing the mount-render copies
  // is safe. Frozen via a ref (not useMemo) so React can never recompute it.
  const actionsRef = useRef(null);
  if (actionsRef.current === null) {
    actionsRef.current = {
      setActiveSlideTitle,
      setModifiedSlideTitle,
      setActiveSlideLayout,
      setHasSlideAudio,
      setSelectModeStatus,
      setBlockOutlines,
      setShowBlockTree,
      setActiveSlideGrid,
      setHoveredNode,
      setHoveredBlock,
      setActiveSelectedBlocks,
      setActiveSelectedGroups,
      setActiveAvailableTools,
      setActiveCustomLayoutLock,
      registerToggleGridFunction,
      triggerToggleGrid,
      registerModifySlideTitle,
      triggerModifySlideTitle,
      registerGroupFunction,
      triggerGroup,
      registerUngroupFunction,
      triggerUngroup,
      registerToggleBlockOutlinesFunction,
      triggerBlockOutlines,
      registerCurrentEditorInstance,
      currentEditorInstanceRef,
      registerCurrentSlideRef,
      currentSlideRef,
      registerBlockTreeUpdateFunction,
      triggerBlockTreeUpdate,
      registerBlockOrderFunction,
      triggerBlockOrder,
      registerBlockDuplicateFunction,
      triggerBlockDuplicate,
      registerSelectClickHandler,
      triggerSelectClick,
      registerStartSelectModeFunction,
      triggerStartSelectMode,
      registerEndSelectModeFunction,
      triggerEndSelectMode,
      registerDeleteBlocksFunction,
      triggerDeleteBlocks,
      registerAddBlockFunction,
      triggerAddBlock,
      registerHideBlockFunction,
      triggerHideBlock,
      registerHideGroupFunction,
      triggerHideGroup,
      registerLockSlideLayoutFunction,
      triggerLockSlideLayout,
      registerMoveBlockToGroupFunction,
      triggerMoveBlockToGroup,
    };
  }
  const actions = actionsRef.current;

  // Volatile UI state — identity changes only when one of these values changes,
  // so Toolbar/BlockTree re-render exactly when (and only when) needed.
  const state = useMemo(
    () => ({
      activeSlideTitle,
      modifiedSlideTitle,
      activeSlideLayout,
      hasSlideAudio,
      selectModeStatus,
      blockOutlines,
      showBlockTree,
      activeSlideGrid,
      hoveredNode,
      hoveredBlock,
      activeSelectedBlocks,
      activeSelectedGroups,
      activeAvailableTools,
      activeCustomLayoutLock,
    }),
    [
      activeSlideTitle,
      modifiedSlideTitle,
      activeSlideLayout,
      hasSlideAudio,
      selectModeStatus,
      blockOutlines,
      showBlockTree,
      activeSlideGrid,
      hoveredNode,
      hoveredBlock,
      activeSelectedBlocks,
      activeSelectedGroups,
      activeAvailableTools,
      activeCustomLayoutLock,
    ],
  );

  return (
    <SlideActionsContext.Provider value={actions}>
      <SlideStateContext.Provider value={state}>
        {children}
      </SlideStateContext.Provider>
    </SlideActionsContext.Provider>
  );
};
